import type { PureQueryOptions } from '@apollo/client';
import { useQuery } from '@apollo/client';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Outlet, useOutletContext, useParams } from 'react-router';
import invariant from 'tiny-invariant';
import { ApolloProviderV4 } from '~/apollo/client-v4';
import { graphql } from '~/apollo/generated/v4';
import type { UpdatePaleomapPageQuery } from '~/apollo/generated/v4/graphql';
import { NotFound } from '~/components/common/NotFound';
import { PageHeading } from '~/components/common/PageHeading';
import { SpinnerPlaceholder } from '~/components/common/SpinnerPlaceholder';
import { useBreadcrumb } from '~/components/layout/Breadcrumb';
import { Button } from '~/components/ui/button';
import { CollapsibleCard } from '~/components/ui/collapse';
import { DeletePaleoMap } from '~/components/upload/paleomap/paleomap-outcrop-manager/DeletePaleomap';
import { UploadPaleoMapNavigation } from '~/routes/upload/paleomap/$paleoMapId/__PaleoMapNavigation';
import { createRefetchQueries } from '~/utils/graphql';

const UPDATE_PALEOMAP_PAGE = graphql(`
  query UpdatePaleomapPage($id: ID!) {
    paleoMapGet(id: $id) {
      ...paleoMapParts
      outcrops {
        id
        name
        region {
          id
          name
        }
      }
      age {
        ...lithostratAgeParts
      }
      pictures {
        ...soManagerPictureParts
      }
    }
  }
`);

type OutletContext = {
  paleoMap: NonNullable<UpdatePaleomapPageQuery['paleoMapGet']>;
  refetchQueries: PureQueryOptions[];
};

export default function UpdatePaleomapRoute() {
  return (
    <ApolloProviderV4>
      <PageInner />
    </ApolloProviderV4>
  );
}

function PageInner() {
  useBreadcrumb('routes/upload/paleomap/$paleoMapId', 'Update Palaeomap');

  const params = useParams();
  invariant(params.paleoMapId, 'paleoMapId param required');
  const paleoMapId = parseInt(params.paleoMapId);

  const { loading, data } = useQuery(UPDATE_PALEOMAP_PAGE, {
    variables: { id: paleoMapId },
  });

  const refetchQueries = createRefetchQueries([
    {
      query: UPDATE_PALEOMAP_PAGE,
      variables: { id: paleoMapId },
    },
  ]);

  const paleoMap = data?.paleoMapGet;

  if (loading) return <SpinnerPlaceholder />;
  if (!paleoMap) return <NotFound />;

  const outletContext: OutletContext = {
    paleoMap: paleoMap,
    refetchQueries,
  };

  return (
    <div className="space-y-4">
      <PageHeading>Update Palaeomap</PageHeading>
      <UploadPaleoMapNavigation id={paleoMapId} />
      <Outlet context={outletContext} />

      <CollapsibleCard title="Delete Paleomap">
        <div className="text-center">
          <DeletePaleoMap
            paleoMapId={paleoMapId}
            refetchQueries={refetchQueries}
          >
            {showDeleteModal => (
              <Button
                type="button"
                color="ghost"
                onClick={showDeleteModal}
                className="text-error hover:text-error"
                startIcon={<FontAwesomeIcon icon={faTrash} />}
              >
                Delete Paleomap
              </Button>
            )}
          </DeletePaleoMap>
        </div>
      </CollapsibleCard>
    </div>
  );
}

export function useUpdatePaleoMapOutletContext() {
  return useOutletContext<OutletContext>();
}
